import { AsyncPipe, NgClass } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import {
    Component,
    computed,
    inject,
    signal,
    ChangeDetectionStrategy
} from "@angular/core";
import { IVisualEmbedConfiguration, models } from "powerbi-client";
import { PowerBIEmbedModule } from "powerbi-client-angular";
import {
    catchError,
    combineLatest,
    map,
    Observable,
    of,
    shareReplay,
    throwError
} from "rxjs";
import { BusySpinnerComponent } from "~app/common/components/busy/busy-spinner/busy-spinner.component";
import { PillboxComponent } from "~app/common/components/pillbox/pillbox.component";
import { environment } from "~environments/environment";
import { DashboardService } from "../../dashboard.service";
import { ReportEmbedInfo, ReportEmbedService } from "./report-embed.service";

const {
    powerBIConfig: {
        hostname,
        rotstaendeLager,
        avrakningshjalpen
    }
} = environment;

type VisualItem = {
    reportId: string;
    pageName: string;
    id: string;
    token$: Observable<ReportEmbedInfo | null>;
};

@Component({
    selector: "rask-insikten-reports",
    imports: [
        PowerBIEmbedModule,
        AsyncPipe,
        NgClass,
        BusySpinnerComponent,
        PillboxComponent
    ],
    templateUrl: "./insikten-reports.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ["./insikten-reports.component.scss"]
})
export class InsiktenReportsComponent {
    protected readonly webAvrakningshjalpenUrl$: Observable<string>;
    protected readonly webRotstaendeLagerUrl$: Observable<string>;

    protected readonly visualItems: VisualItem[];

    protected readonly loading = computed(() => this.visualsLoaded() < this.visualItems.length);

    protected readonly eventHandlers = new Map([
        [
            "rendered",
            () => this.visualsLoaded.update(n => n + 1)
        ]
    ]);

    private readonly visualsLoaded = signal(0);

    private readonly reportEmbedService = inject(ReportEmbedService);
    private readonly dashboardService = inject(DashboardService);

    private readonly userName$ = this.dashboardService.user$.pipe(
        map(user => user.username)
    );

    constructor() {
        this.webAvrakningshjalpenUrl$ = this.userName$.pipe(
            map(userName => this.createAvrakningshjalpenUrl(environment.powerBIConfig.avrakningshjalpen.webUrl, userName))
        );

        this.webRotstaendeLagerUrl$ = this.userName$.pipe(
            map(userName => this.createRotstaendeLagerUrl(environment.powerBIConfig.rotstaendeLager.webUrl, userName))
        );

        this.visualItems = [
            rotstaendeLager,
            avrakningshjalpen
        ].map((report) => {
            const tokenAndUrl$ = this.reportEmbedService.getTokenAndUrl(report.reportId).pipe(
                catchError((error: unknown) => {
                    if (error instanceof HttpErrorResponse) {
                        return of(null);
                    }

                    return throwError(() => error);
                }),
                shareReplay(1)
            );

            return Object.values(report.visuals).map<VisualItem>(visual => ({
                reportId: report.reportId,
                pageName: report.pageName,
                id: visual,
                token$: tokenAndUrl$
            }));
        }).flat();
    }

    protected getConfig(visual: VisualItem): Observable<IVisualEmbedConfiguration | null> {
        return combineLatest([
            visual.token$,
            this.userName$
        ]).pipe(
            map(([tokenAndUrl, userName]) => this.createConfig(visual, userName, tokenAndUrl))
        );
    }

    private createConfig(visual: VisualItem, userName: string, tokenAndUrl: ReportEmbedInfo): IVisualEmbedConfiguration {
        if (tokenAndUrl === null) {
            this.visualsLoaded.update(n => n + 1);
            return null;
        }

        const config: IVisualEmbedConfiguration = {
            visualName: visual.id,
            accessToken: tokenAndUrl.accessToken,
            embedUrl: tokenAndUrl.embedUrl,
            id: visual.reportId,
            type: "visual",
            permissions: models.Permissions.All,
            tokenType: models.TokenType.Aad,
            hostname: hostname,
            pageName: visual.pageName,
            viewMode: models.ViewMode.View,
            settings: {
                layoutType: models.LayoutType.Custom,
                customLayout: {
                    displayOption: models.DisplayOption.FitToPage
                },
                visualSettings: {
                    visualHeaders: [{ settings: { visible: false } }]
                }
            }
        };

        config.filters = this.createFilters(userName);

        return config;
    }

    private createAvrakningshjalpenUrl(webUrl: string, userName: string): string {
        const queryParams = `filter=Virkesk_x00f6_pare/Virkesk_x00f6_pareKod eq '${userName}'`;
        return [
            webUrl,
            queryParams
        ].join("?");
    }

    private createRotstaendeLagerUrl(webUrl: string, userName: string): string {
        const queryParams = `filter=frptTraktbankVirkesk_x00f6_p/AnsvarigVirkesk_x00f6_pareKod eq '${userName}'`;
        return [
            webUrl,
            queryParams
        ].join("?");
    }

    private createFilters(userName: string): models.ReportLevelFilters[] {
        return [
            {
                $schema: "https://powerbi.com/product/schema#basic",
                target: {
                    table: "frptTraktbankVirkesköp",
                    column: "AnsvarigVirkesköpareKod"
                },
                filterType: models.FilterType.Basic,
                operator: "In",
                values: [userName]
            },
            {
                $schema: "https://powerbi.com/product/schema#basic",
                target: {
                    table: "Virkesköpare",
                    column: "VirkesköpareKod"
                },
                filterType: models.FilterType.Basic,
                operator: "In",
                values: [userName]
            }
        ];
    }
}
